import type { MusicBands } from './MusicEngine';
import { fft, hannWindow } from './fft';

const FFT_SIZE = 512;
const FREQ_BINS = FFT_SIZE / 2;

export type FrameAnalysis = {
  bands: MusicBands;
  freq: Uint8Array;
  wave: Float32Array;
};

export type AnalyzerState = {
  smooth: { kick: number; bass: number; mids: number; highs: number; energy: number };
  peak: { kick: number; bass: number; mids: number; highs: number; energy: number };
  lastEnergy: number;
  re: Float32Array;
  im: Float32Array;
  window: Float32Array;
  freq: Uint8Array;
  wave: Float32Array;
};

export function createAnalyzerState(): AnalyzerState {
  return {
    smooth: { kick: 0, bass: 0, mids: 0, highs: 0, energy: 0 },
    peak: { kick: 0.14, bass: 0.14, mids: 0.14, highs: 0.14, energy: 0.14 },
    lastEnergy: 0,
    re: new Float32Array(FFT_SIZE),
    im: new Float32Array(FFT_SIZE),
    window: hannWindow(FFT_SIZE),
    freq: new Uint8Array(FREQ_BINS),
    wave: new Float32Array(FFT_SIZE),
  };
}

/** Mix AudioBuffer down to mono Float32Array. */
export function audioBufferToMono(buffer: AudioBuffer): Float32Array {
  const { numberOfChannels, length } = buffer;
  if (numberOfChannels === 1) {
    const out = new Float32Array(length);
    out.set(buffer.getChannelData(0));
    return out;
  }
  const out = new Float32Array(length);
  const scale = 1 / numberOfChannels;
  for (let c = 0; c < numberOfChannels; c++) {
    const ch = buffer.getChannelData(c);
    for (let i = 0; i < length; i++) out[i] += ch[i] * scale;
  }
  return out;
}

/**
 * Analyze mono PCM at timeSec (center of window).
 * Mutates state; returns views into state buffers (valid until next call).
 */
export function analyzeAt(
  mono: Float32Array,
  sampleRate: number,
  timeSec: number,
  sensitivity: number,
  state: AnalyzerState,
): FrameAnalysis {
  const center = Math.floor(timeSec * sampleRate);
  const half = FFT_SIZE >> 1;
  const start = center - half;

  for (let i = 0; i < FFT_SIZE; i++) {
    const src = start + i;
    const s = src >= 0 && src < mono.length ? mono[src] : 0;
    state.wave[i] = s;
    state.re[i] = s * state.window[i];
    state.im[i] = 0;
  }

  fft(state.re, state.im);

  // magnitude → 0..255 (adaptive scale, similar spirit to AnalyserNode)
  let maxMag = 1e-6;
  const mags = new Float32Array(FREQ_BINS);
  for (let i = 0; i < FREQ_BINS; i++) {
    const mag = Math.hypot(state.re[i], state.im[i]);
    mags[i] = mag;
    if (mag > maxMag) maxMag = mag;
  }
  const scale = 255 / Math.max(maxMag, 4);
  for (let i = 0; i < FREQ_BINS; i++) {
    const v = Math.min(255, mags[i] * scale * 1.35);
    state.freq[i] = v < 1 ? 0 : v;
  }

  const n = FREQ_BINS;
  const avg = (from: number, to: number) => {
    let s = 0;
    let c = 0;
    const a = Math.max(0, Math.floor(from));
    const b = Math.min(n, Math.ceil(to));
    for (let i = a; i < b; i++) {
      s += state.freq[i];
      c++;
    }
    return c ? s / c / 255 : 0;
  };

  const kickRaw = avg(0, n * 0.04);
  const bassRaw = avg(n * 0.04, n * 0.12);
  const midsRaw = avg(n * 0.12, n * 0.4);
  const highsRaw = avg(n * 0.4, n * 0.9);
  const energyRaw = kickRaw * 0.34 + bassRaw * 0.3 + midsRaw * 0.22 + highsRaw * 0.14;
  const curve = (x: number) => Math.pow(Math.min(1, Math.max(0, x)), 0.85);

  const smoothBand = (
    key: keyof AnalyzerState['smooth'],
    target: number,
    up = 0.28,
    down = 0.1,
  ) => {
    const prev = state.smooth[key];
    const next = target > prev ? prev + (target - prev) * up : prev + (target - prev) * down;
    state.smooth[key] = next;
    state.peak[key] = Math.max(state.peak[key] * 0.995, next, 0.12);
    return Math.min(1, next / state.peak[key]);
  };

  const boost = Math.max(0.4, Math.min(2.2, sensitivity));
  const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

  const kick = clamp01(smoothBand('kick', curve(kickRaw), 0.35, 0.12) * boost);
  const bass = clamp01(smoothBand('bass', curve(bassRaw), 0.3, 0.1) * boost);
  const mids = clamp01(smoothBand('mids', curve(midsRaw), 0.25, 0.1) * boost);
  const highs = clamp01(smoothBand('highs', curve(highsRaw), 0.22, 0.1) * boost);
  const energy = clamp01(smoothBand('energy', curve(energyRaw), 0.22, 0.09) * boost);

  const pulse = Math.max(0, energy - state.lastEnergy) * 4;
  state.lastEnergy = energy;
  const beat =
    pulse > 0.12 || (kick > 0.72 && pulse > 0.04) ? Math.min(1, pulse + kick * 0.3) : 0;

  // wave normalized -1..1 for viz
  for (let i = 0; i < FFT_SIZE; i++) {
    state.wave[i] = Math.max(-1, Math.min(1, state.wave[i]));
  }

  return {
    bands: { kick, bass, mids, highs, energy, pulse: Math.min(1, pulse), beat },
    freq: state.freq,
    wave: state.wave,
  };
}

export async function decodeAudioFromUrl(url: string): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Audio fetch failed: ${res.status}`);
  const ab = await res.arrayBuffer();
  return decodeArrayBuffer(ab);
}

export async function decodeAudioFromFile(file: File): Promise<AudioBuffer> {
  const ab = await file.arrayBuffer();
  return decodeArrayBuffer(ab);
}

async function decodeArrayBuffer(ab: ArrayBuffer): Promise<AudioBuffer> {
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AC();
  try {
    // copy — decodeAudioData may detach
    const copy = ab.slice(0);
    return await ctx.decodeAudioData(copy);
  } finally {
    void ctx.close();
  }
}
